// components/Gacha/GachaBoxCategory.jsx
import { useEffect, useState } from 'react';
import {
    Box, Grid, Button, Typography, Card, CardContent,
    CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, List, ListItem, ListItemText, Chip, Pagination,
    useTheme, useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

export default function GachaBoxCategory() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [selectedTier, setSelectedTier] = useState(null);
    const [titleList, setTitleList] = useState([]);

    const [page, setPage] = useState(1);
    const itemsPerPage = 8;
    const totalPages = Math.ceil(titleList.length / itemsPerPage);

    /* --------------------------- fetch --------------------------- */
    useEffect(() => {
        api.get('/api/rewards/tier-probability/')
            .then(res => setCategories(res.data.tiers || []))
            .catch(err => console.error('카테고리 로딩 실패:', err))
            .finally(() => setLoading(false));
    }, []);

    /* ------------------- 모바일 전용 헬퍼 문구 ------------------- */
    const helperText = isMobile
        ? (
            <>
                원하는 등급의 칭호 박스를 골라,<br />
                어떤 칭호가 나올지 확인해보세요!<br />
                (중복 가능)
            </>
        )
        : '원하는 등급의 칭호 박스를 골라, 어떤 칭호가 나올지 확인해보세요! (중복 가능)';

    /* ------------------- handlers ------------------- */
    const handleCategorySelect = (cat) =>
        navigate('/main/gacha/luckybox/draw', { state: { category: cat } });

    const handleOpenTitles = async (tier) => {
        try {
            const res = await api.get('/api/rewards/titles/list/');
            setTitleList(res.data.titles.filter(t => t.tier === tier));
            setSelectedTier(tier);
            setPage(1);
            setOpen(true);
        } catch (err) {
            console.error('칭호 목록 로딩 실패:', err);
            alert('칭호를 불러오는 데 실패했습니다.');
        }
    };

    /* ------------------- UI ------------------- */
    return (
        <Box sx={{ p: isMobile ? 2 : 4 }}>
            <Typography variant="h6" gutterBottom>
                🎁 칭호 럭키박스
            </Typography>

            <Typography
                variant="body1"
                sx={{ mb: 2, fontSize: { xs: '0.9rem', sm: '1rem' } }}
            >
                {helperText}
            </Typography>

            {loading ? (
                <Box sx={{ mt: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={isMobile ? 1.5 : 2.5} justifyContent="center">
                    {categories.map(cat => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={cat.tier}>
                            <Card
                                sx={{
                                    border: '2px solid',
                                    borderColor: `${cat.color}.main`,
                                    textAlign: 'center',
                                    '&:hover': { boxShadow: 4 },
                                }}
                            >
                                <CardContent sx={{ px: isMobile ? 1.5 : 2.5, py: isMobile ? 2 : 3 }}>
                                    {/* 등급 제목 */}
                                    <Typography
                                        sx={{ fontSize: { xs: 'h6.fontSize', sm: 'h5.fontSize' }, fontWeight: 600 }}
                                        color={`${cat.color}.main`}
                                    >
                                        {cat.tier}
                                    </Typography>

                                    {/* 가격 */}
                                    <Typography sx={{ mb: 1, fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                                        {Number(cat.price ?? 0).toLocaleString()} Leaf🌿
                                    </Typography>

                                    {/* 선택 버튼 */}
                                    <Button
                                        variant="contained"
                                        color={cat.color}
                                        fullWidth
                                        size={isMobile ? 'small' : 'medium'}
                                        disabled={!cat.enabled}
                                        onClick={() => handleCategorySelect(cat)}
                                    >
                                        {cat.enabled ? '선택' : '비활성화됨'}
                                    </Button>

                                    {/* 칭호 보기 */}
                                    <Button
                                        variant="text"
                                        fullWidth
                                        size="small"
                                        onClick={() => handleOpenTitles(cat.tier)}
                                        sx={{ mt: 1 }}
                                    >
                                        칭호 보기
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* ---- 칭호 목록 모달 ---- */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>🎖️ {selectedTier} 등급의 칭호 목록</DialogTitle>

                <DialogContent dividers>
                    {titleList.length === 0 ? (
                        <Typography>칭호가 없습니다.</Typography>
                    ) : (
                        <>
                            <List>
                                {titleList
                                    .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                                    .map(title => (
                                        <ListItem key={title.id}>
                                            <Chip
                                                label={title.is_owned ? '보유중' : '미보유'}
                                                size="small"
                                                sx={{
                                                    mr: 2,
                                                    backgroundColor: title.is_owned ? 'success.main' : 'grey.500',
                                                    color: '#fff',
                                                }}
                                            />
                                            <ListItemText primary={title.name} />
                                        </ListItem>
                                    ))}
                            </List>
                            {totalPages > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(e, v) => setPage(v)}
                                        size="small"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)}>닫기</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
